import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../store';
import { useAppState } from '../../store/appstate';
import { useGuidedSetupStore } from '../../store/guidedSetup/guidedSetupStore';
import sessionActions from '../../store/session/actions';
import { SessionOrganization } from '../../types/SessionUser';
import { setupStepIds, SetupSteps, SetupStepStatus } from '../helpers/guidedSetup';
import { useIsGuidedSetupEnabled, useIsGuidedSetupLoaded } from './useIsGuidedSetupEnabled';

const allStepsDone = Object.fromEntries(
    setupStepIds.map(id => [id, SetupStepStatus.Done])
) as SetupSteps;

/**
 * Where each step of the guided setup stands for the user's own organization; the selected organization never
 * matters. The steps come with the organization (GET organizations/{id}); the merchants, which the KYC and bank
 * account actions need, are loaded once while the setup is on. Call refresh to re-read both, e.g. when the user comes
 * back from a Bits tab.
 */
export const useGuidedSetup = () => {
    const dispatch = useAppDispatch();
    const isEnabled = useIsGuidedSetupEnabled();
    const isSetupLoaded = useIsGuidedSetupLoaded();
    const setupSteps = useAppState<SetupSteps | undefined>(s => s.session.guidedSetup?.setupSteps);
    const organization = useAppState<SessionOrganization | undefined>(
        s => s.session.user?.organization
    );
    const organizationId = organization?.id;
    const { isLoaded, loadError, merchants, loadMerchants } = useGuidedSetupStore();

    const canLoad = isEnabled && !!organizationId;

    useEffect(() => {
        if (!canLoad || !organizationId) return;

        // Every component using the hook runs this effect; the store tells whether another one already started the load.
        const { isLoading, isLoaded: hasLoaded } = useGuidedSetupStore.getState();
        if (isLoading || hasLoaded) return;

        loadMerchants(organizationId);
    }, [canLoad, organizationId, loadMerchants]);

    const refresh = useCallback(() => {
        if (!canLoad || !organizationId) return;

        dispatch(sessionActions.getOrganization(organizationId, false));
        loadMerchants(organizationId);
    }, [canLoad, organizationId, loadMerchants, dispatch]);

    return useMemo(() => {
        // The backend leaves the steps out once every step is done.
        const isDone = !setupSteps;
        const steps = setupSteps ?? allStepsDone;

        return {
            /** Whether the organization is in the setup (shouldRedirectToGetStarted). */
            isEnabled,
            /** Whether it is known yet if the organization is in the setup. */
            isSetupLoaded,
            /** The user's own organization, which the setup is for. */
            organization,
            /** Whether the setup data is still being loaded for the first time. */
            isLoading: isEnabled && !isLoaded,
            loadError,
            // Organizations in the setup have one merchant; with more, the first one is set up.
            merchant: merchants[0],
            steps,
            isDone,
            /** How many steps are done. Waiting steps do not count. */
            doneCount: setupStepIds.filter(id => steps[id] === SetupStepStatus.Done).length,
            refresh,
        };
    }, [
        merchants,
        setupSteps,
        isEnabled,
        isSetupLoaded,
        organization,
        isLoaded,
        loadError,
        refresh,
    ]);
};
